import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Container,
  useMediaQuery,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { io } from 'socket.io-client';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import { useTheme } from '@mui/material/styles';
import { getCurrentUser } from '../../models/Account';

const navLinks = [
  { label: 'TRANG CHỦ', path: '/' },
  { label: 'LƯỢC SỬ', path: '/luocsu' },
  { label: 'CHIẾN KÝ', path: '/chienky' },
  { label: 'HỒI ÂM', path: '/hoiam' },
  { label: 'PHẢN HỒI', path: '/phanhoi' },
  { label: 'GIỚI THIỆU', path: '/gioithieu' },
];

function Header() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [avatar, setAvatar] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notifLimit, setNotifLimit] = useState(5);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef();

  // --- Load user và avatar ---
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getCurrentUser().then((user) => {
        setUser(user);
        setAvatar(
          user?.avatar
            ? `${process.env.REACT_APP_STATIC_URL}${user.avatar}`
            : `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=random`
        );
      });
    } else {
      setUser(null);
    }
  }, [location]);

  // --- Scroll hide/show header ---
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.pageYOffset;

      if (currentScrollY - lastScrollY > 70 && currentScrollY > 100) {
        setShowHeader(false);
      } else if (lastScrollY - currentScrollY > 10) {
        setShowHeader(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // --- Socket.io và notifications ---
  useEffect(() => {
    if (!user) return;

    fetch(`${process.env.REACT_APP_API_URL}/notifications/${user.id}`)
      .then(res => res.json())
      .then(data => {
        setNotifications(data);
        setUnreadCount(data.filter(n => n.read_status === 0).length);
      });
  }, [user]);

  // --- Socket.io ---
  useEffect(() => {
    if (!socketRef.current) {
      // sử dụng HTTPS và đúng path /socket.io
      socketRef.current = io(process.env.REACT_APP_SOCKET_URL, {
        transports: ['websocket', 'polling'],
      });
      console.log("Socket created:", socketRef.current);
    }
    return () => {
      socketRef.current && socketRef.current.disconnect();
    }
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!user || !socket) return;

    const handleConnect = () => {
      socket.emit("join_user_room", `user_${user.id}`);
    };

    const handler = (notif) => {
      setNotifications(prev => [notif, ...prev]);
      setUnreadCount(c => c + 1);
    };

    socket.on("connect", handleConnect);
    socket.on("new_comment", handler);

    if (socket.connected) handleConnect();

    return () => {
      socket.off("connect", handleConnect);
      socket.off("new_comment", handler);
    };
  }, [user]);


  const handleClickNotification = async (n) => {
    if (n.target_type == 'article') navigate(`/articles/${n.target_id}`);
    else if (n.target_type == 'feeling') navigate(`/feelings/${n.target_id}`);
    else {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/comments/${n.parent_id}`);
        const parentComment = await res.json();

        if (parentComment.target_type === 'article') {
          navigate(`/articles/${parentComment.target_id}`);
        } else if (parentComment.target_type === 'feeling') {
          navigate(`/feelings/${parentComment.target_id}`);
        } else {
          navigate('/');
        }
      } catch (err) {
        throw err
      }
    }
  };

  const loadMoreNotifications = () => {
    setNotifLimit(prev => prev + 5);
  };

  useEffect(() => {
    if (showNotifPanel && user) {
      fetch(`${process.env.REACT_APP_API_URL}/notifications/${user.id}/read`, { method: 'PUT' })
        .then(() => setUnreadCount(0));
    }
  }, [showNotifPanel, user]);

  const handleClearNotification = () => {
    setShowNotifPanel(prev => !prev);
    setNotifLimit(5);
  };

  // Màu nền đỏ thẫm
  const mainRed = '#8a0000';
  const hoverRed = '#b00000';
  const textLight = '#fff';
  const beigeHover = '#f5e6a4';

  return (
    <Box>
      {/* --- Header bars --- */}
      <Box
        sx={{
          position: 'fixed',
          top: showHeader ? 0 : isMobile ? -96 : -168,
          left: 0,
          right: 0,
          transition: 'top 0.3s',
          zIndex: 1100,
          ...(isMobile && { maxWidth: '100vw', overflowX: 'hidden' })
        }}
      >
        <AppBar position="relative" elevation={0} sx={{ height: 96, justifyContent: 'center', backgroundColor: mainRed, color: textLight }}>
          <Container maxWidth="xl">
            <Toolbar sx={{ justifyContent: 'space-between', height: '100%' }}>
              <Box
                sx={{
                  cursor: 'pointer',
                  '&:hover .titleText': { color: hoverRed, textDecoration: 'underline' },
                }}
                onClick={() => navigate('/')}
              >
                <img
                  src={`${process.env.REACT_APP_STATIC_URL}/uploads/logo/text_nhat.png`}
                  alt="TIẾNG VỌNG SỬ VIỆT"
                  style={{ height: 96, maxWidth: isMobile ? 160 : 'none', objectFit: 'contain' }}
                />
              </Box>

              {isMobile ? (
                <IconButton onClick={() => setDrawerOpen(!drawerOpen)} sx={{ color: textLight }}>
                  {drawerOpen ? <CloseIcon /> : <MenuIcon />}
                </IconButton>
              ) : (
                <Box display="flex" alignItems="center" gap={1.5}>
                  {user ? (
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer' }} onClick={() => navigate('/account')}>
                        <img src={avatar} alt="Avatar" style={{ width: 40, height: 40, borderRadius: '50%' }} />
                        <Typography fontWeight={600} sx={{ color: textLight, userSelect: 'none' }}>{user.username}</Typography>
                      </Box>

                      <Box sx={{ position: 'relative' }}>
                        <IconButton onClick={() => {
                          handleClearNotification();
                        }}
                          sx={{ color: textLight }}>
                          <Badge badgeContent={unreadCount} color="error">
                            <NotificationsIcon />
                          </Badge>
                        </IconButton>

                        {showNotifPanel && (
                          <Box sx={{
                            position: 'absolute',
                            top: 48,
                            right: 0,
                            width: 360,
                            maxHeight: 400,
                            overflowY: 'auto',
                            backgroundColor: '#fff',
                            color: '#000',
                            border: `1px solid ${hoverRed}`,
                            borderRadius: 1,
                            zIndex: 1500,
                          }}>
                            <List>
                              {notifications.slice(0, notifLimit).map((n) => (
                                <ListItem key={n.id} divider button onClick={() => handleClickNotification(n)}>
                                  <ListItemText
                                    primary={n.text}
                                    secondary={`Từ ${(n.users || []).map(u => u.username).join(', ')} - ${new Date(n.created_at).toLocaleString()}`}
                                  />
                                </ListItem>
                              ))}
                              {notifLimit < notifications.length && (
                                <ListItem button onClick={loadMoreNotifications} sx={{ justifyContent: 'center' }}>
                                  <Typography sx={{ color: mainRed, fontWeight: 600 }}>Xem thêm</Typography>
                                </ListItem>
                              )}
                            </List>
                          </Box>
                        )}
                      </Box>

                      {user.role === 'admin' && (
                        <IconButton onClick={() => navigate('/dashboard')} sx={{ ml: 1, color: textLight, '&:hover': { color: beigeHover } }} title="Quản trị">
                          <HomeIcon />
                        </IconButton>
                      )}

                      <Button
                        variant="contained"
                        onClick={() => {
                          localStorage.removeItem('token');
                          setUser(null);
                          navigate('/');
                        }}
                        sx={{
                          width: 120,
                          height: 48,
                          borderRadius: 0,
                          fontSize: 13,
                          fontWeight: 600,
                          fontFamily: 'Helvetica, Arial, sans-serif',
                          backgroundColor: '#fff',
                          color: mainRed,
                          '&:hover': { backgroundColor: beigeHover, color: mainRed },
                        }}
                      >
                        ĐĂNG XUẤT
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      onClick={() => navigate('/login')}
                      variant="contained"
                      sx={{
                        width: 200,
                        height: 48,
                        borderRadius: 0,
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        backgroundColor: '#fff',
                        color: mainRed,
                        '&:hover': { backgroundColor: beigeHover, color: mainRed },
                      }}
                    >
                      ĐĂNG NHẬP / ĐĂNG KÝ
                    </Button>
                  )}
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>

        {!isMobile && (
          <AppBar
            position="relative"
            elevation={0}
            sx={{
              backgroundColor: '#fbf7e4',
              height: 72,
              justifyContent: 'center',
            }}
          >
            <Container maxWidth="xl" sx={{ height: '100%' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" height="100%">
                <Typography
                  variant="h2"
                  onClick={() => navigate('/')}
                  sx={{
                    fontWeight: 700,
                    color: textLight,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                </Typography>

                <Box
                  display="flex"
                  alignItems="center"
                  sx={{ height: '100%', flexGrow: 1, justifyContent: 'center' }}
                >
                  {navLinks.map(({ label, path }) => (
                    <Box
                      key={label}
                      onClick={() => navigate(path)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        px: 3,
                        cursor: 'pointer',
                        color: '#000',
                        fontWeight: 600,
                        fontFamily: '"Be Vietnam Pro", sans-serif',
                        borderBottom:
                          location.pathname === path
                            ? `4px solid ${hoverRed}`
                            : '4px solid transparent',
                        '&:hover': { borderBottom: `4px solid ${hoverRed}` },
                        userSelect: 'none',
                      }}
                    >
                      <Typography variant="body1" fontWeight="bold">
                        {label}
                      </Typography>
                    </Box>
                  ))}

                  <Box
                    onClick={() => navigate('/timkiem')}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      px: 3,
                      cursor: 'pointer',
                      color: '#000',
                      fontWeight: 600,
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      borderBottom:
                        location.pathname === '/timkiem'
                          ? `4px solid ${hoverRed}`
                          : '4px solid transparent',
                      '&:hover': { borderBottom: `4px solid ${hoverRed}` },
                      ml: 'auto',
                      userSelect: 'none',
                    }}
                  >
                    <SearchIcon sx={{ mr: 1 }} />
                    <Typography variant="body1" fontWeight="bold">TÌM KIẾM</Typography>
                  </Box>
                </Box>

                <Box width={40} />
              </Box>
            </Container>
          </AppBar>
        )}
      </Box>

      {/* Drawer mobile */}
      {/* Drawer mobile */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            width: drawerOpen ? 220 : 0,
            backgroundColor: mainRed,
            overflowX: 'hidden',
            transition: 'width 0.3s ease-in-out',
            zIndex: 1400,
            boxShadow: drawerOpen ? 5 : 0,
            color: textLight,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {/* --- Phần trên --- */}
          <Box sx={{ p: 2 }}>
            <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: textLight }}>
              <CloseIcon />
            </IconButton>

            {user ? (
              <Box display="flex" flexDirection="column" gap={1} mb={2} alignItems="center">
                <img src={avatar} alt="Avatar" style={{ width: 60, height: 60, borderRadius: '50%' }} />
                <Typography fontWeight={600} sx={{ color: textLight }}>
                  {user.username}
                </Typography>

                {user.role === 'admin' && (
                  <Button
                    variant="contained"
                    startIcon={<HomeIcon />}
                    onClick={() => { navigate('/dashboard'); setDrawerOpen(false); }}
                    sx={{
                      width: '100%',
                      borderRadius: 0,
                      fontWeight: 600,
                      fontFamily: 'Helvetica, Arial, sans-serif',
                      backgroundColor: '#fff',
                      color: mainRed,
                      '&:hover': { backgroundColor: beigeHover, color: mainRed },
                    }}
                  >
                    Quản trị
                  </Button>
                )}

                {/* --- Notifications như desktop --- */}
                <Box sx={{ width: '100%', position: 'relative' }}>
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <IconButton
                      onClick={handleClearNotification}
                      sx={{
                        color: textLight,
                      }}
                    >
                      <Badge badgeContent={unreadCount} color="error">
                        <NotificationsIcon />
                      </Badge>
                    </IconButton>
                  </Box>

                  {showNotifPanel && (
                    <Box sx={{
                      position: 'absolute',
                      top: '48px',
                      left: 0,
                      width: '100%',
                      maxHeight: 300,
                      overflowY: 'auto',
                      backgroundColor: '#fff',
                      border: `1px solid ${hoverRed}`,
                      borderRadius: 1,
                      boxShadow: 3,
                      zIndex: 1500,
                    }}>
                      <List>
                        {notifications.slice(0, notifLimit).map(n => (
                          <ListItem
                            key={n.id}
                            divider
                            button
                            onClick={() => {
                              handleClickNotification(n);
                              setDrawerOpen(false);
                              console.log(n);
                            }}
                          >
                            <ListItemText
                              primary={n.text}
                              primaryTypographyProps={{ sx: { color: '#000', fontWeight: 600, fontSize: 14, wordBreak: 'break-word' } }}
                              secondary={`Từ ${(n.users || []).map(u => u.username).join(', ')} - ${new Date(n.created_at).toLocaleString()}`}
                            />
                          </ListItem>
                        ))}
                        {notifLimit < notifications.length && (
                          <ListItem button onClick={loadMoreNotifications} sx={{ justifyContent: 'center' }}>
                            <Typography sx={{ color: mainRed, fontWeight: 600 }}>Xem thêm</Typography>
                          </ListItem>
                        )}
                      </List>
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={1} mb={2}>
                <Button
                  onClick={() => navigate('/login')}
                  variant="contained"
                  sx={{
                    width: 200,
                    height: 48,
                    borderRadius: 0,
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    backgroundColor: '#fff',
                    color: mainRed,
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': { backgroundColor: beigeHover, color: mainRed },
                  }}
                >
                  <span>ĐĂNG NHẬP</span>
                  <span>ĐĂNG KÝ</span>
                </Button>
              </Box>
            )}

            <Divider sx={{ mb: 2, borderColor: hoverRed }} />

            <List>
              {navLinks.map(({ label, path }) => (
                <ListItem
                  button
                  key={label}
                  onClick={() => { navigate(path); setDrawerOpen(false); }}
                  sx={{
                    color: textLight,
                    '&:hover': { backgroundColor: hoverRed, color: textLight },
                    userSelect: 'none',
                  }}
                >
                  <ListItemText primary={label} />
                </ListItem>
              ))}
              <ListItem
                button
                onClick={() => { navigate('/timkiem'); setDrawerOpen(false); }}
                sx={{
                  color: textLight,
                  '&:hover': { backgroundColor: hoverRed, color: textLight },
                  userSelect: 'none',
                }}
              >
                <ListItemText primary="TÌM KIẾM" />
              </ListItem>
            </List>
          </Box>

          {/* --- Nút đăng xuất cố định dưới cùng --- */}
          {user && (
            <Box sx={{ p: 2 }}>
              <Button
                variant="contained"
                onClick={() => { localStorage.removeItem('token'); setUser(null); setDrawerOpen(false); navigate('/'); }}
                sx={{
                  width: '100%',
                  borderRadius: 0,
                  fontWeight: 600,
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  backgroundColor: '#fff',
                  color: mainRed,
                  '&:hover': { backgroundColor: beigeHover, color: mainRed },
                }}
              >
                ĐĂNG XUẤT
              </Button>
            </Box>
          )}
        </Box>
      )}


    </Box>
  );
}

export default Header;
